import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import type {
  ApiKeyUsage,
  ApiKeyView,
  ApiRequestRow,
  CreateApiKeyBody,
  CreateApiKeyResult,
  RotateApiKeyResult,
  WebhookDeliveryRow,
  WebhookSubscriptionRow,
} from './types';

export const qk = {
  all: ['api-keys'] as const,
  keys: ['api-keys', 'list'] as const,
  usage: (id: string) => ['api-keys', 'usage', id] as const,
  requests: (apiKeyId?: string) => ['api-keys', 'requests', apiKeyId ?? 'all'] as const,
  webhooks: ['api-keys', 'webhooks'] as const,
  deliveries: (subscriptionId?: string) => ['api-keys', 'deliveries', subscriptionId ?? 'all'] as const,
};

export function useApiKeys(enabled = true) {
  return useQuery({ queryKey: qk.keys, queryFn: () => api<{ items: ApiKeyView[] }>('/api/api-keys').then((r) => r.items), refetchInterval: 60_000, enabled });
}

export function useApiKeyUsage(id: string | undefined) {
  return useQuery({
    queryKey: qk.usage(id ?? ''),
    queryFn: () => api<ApiKeyUsage>(`/api/api-keys/${encodeURIComponent(id!)}/usage`),
    enabled: Boolean(id),
  });
}

export function useApiRequests(apiKeyId?: string, limit = 50, enabled = true) {
  return useQuery({
    queryKey: qk.requests(apiKeyId),
    queryFn: () => api<{ items: ApiRequestRow[] }>('/api/api-keys/requests', { query: { apiKeyId, limit } }).then((r) => r.items),
    refetchInterval: 30_000,
    enabled,
  });
}

export function useWebhookSubscriptions() {
  return useQuery({
    queryKey: qk.webhooks,
    queryFn: () => api<{ items: WebhookSubscriptionRow[] }>('/api/api-keys/webhooks').then((r) => r.items),
    refetchInterval: 60_000,
  });
}

export function useWebhookDeliveries(subscriptionId?: string, limit = 30) {
  return useQuery({
    queryKey: qk.deliveries(subscriptionId),
    queryFn: () =>
      api<{ items: WebhookDeliveryRow[] }>('/api/api-keys/webhooks/deliveries', { query: { subscriptionId, limit } }).then((r) => r.items),
    refetchInterval: 30_000,
  });
}

function useInvalidateAll() {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: qk.all });
}

export function useCreateApiKey() {
  const inv = useInvalidateAll();
  return useMutation({
    mutationFn: (body: CreateApiKeyBody) => api<CreateApiKeyResult>('/api/api-keys', { method: 'POST', body: { ...body } }),
    onSuccess: inv,
  });
}

export function useRotateApiKey() {
  const inv = useInvalidateAll();
  return useMutation({
    mutationFn: (v: { id: string; totpCode: string }) =>
      api<RotateApiKeyResult>(`/api/api-keys/${encodeURIComponent(v.id)}/rotate`, { method: 'POST', body: { totpCode: v.totpCode } }),
    onSuccess: inv,
  });
}

export function useRevokeApiKey() {
  const inv = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api<{ apiKey: ApiKeyView }>(`/api/api-keys/${encodeURIComponent(id)}/revoke`, { method: 'POST' }),
    onSuccess: inv,
  });
}

export function useEnableWebhook() {
  const inv = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api(`/api/api-keys/webhooks/${encodeURIComponent(id)}/enable`, { method: 'POST' }),
    onSuccess: inv,
  });
}

export function useDeleteWebhook() {
  const inv = useInvalidateAll();
  return useMutation({
    mutationFn: (id: string) => api(`/api/api-keys/webhooks/${encodeURIComponent(id)}`, { method: 'DELETE' }),
    onSuccess: inv,
  });
}
